import React, { useState } from 'react'
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import { SelectValidator, ValidatorForm } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import useProductCurrency from '../../../../../hooks/useProductCurrency';
import { getFullName } from '../../../../../lib/personDetails.es6';
import {
  findBlockingClaim,
  blockingClaimMessage,
  blockingClaimLabel
} from '../../../../../lib/existingClaimGuard.es6';
import { getPartnerGuid } from '../../../../../lib/access.es6';
import {
  applyBenefitCaps,
  declaredConditionForMember
} from '../../../../../lib/claimBenefitCaps.es6';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};


function getStyles(name, selectBenefit, theme) {
  return {
    fontWeight:
      selectBenefit.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}


const SelectMember = (props) => {

  console.log("select member props", props)

  const classes = useStyle();
  const theme = useTheme();
  const [selectMember, setSelectMember] = useState('');
  const [selectBenefit, setSelectBenefit] = useState([]);
  // Currency comes from the quote's product, not the partner country
  // (these can differ, e.g. OMK products priced in KES).
  const currencyCode = useProductCurrency(
    props.eventQuote ? props.eventQuote.product_code : null
  );

  // Benefits a claim can actually be logged against.
  const selectableBenefits = (selectMember && selectMember.benefits
    ? selectMember.benefits
    : []
  ).filter(
    (benefit) => benefit.type !== "Rewards" && benefit.type !== "Cashback"
  );

  const applyBenefits = (member, benefits) => {
    const tempClaimInitiation = props.claimInitiationPayload;

    // Map selected benefit types to their full benefit objects
    const selectedBenefitObjects = benefits.map((benefitType) => {
      const fullBenefit = member.benefits.find(b => b.type === benefitType);
      return fullBenefit || { type: benefitType };
    });

    tempClaimInitiation["benefits"] = selectedBenefitObjects;
    tempClaimInitiation["dependant_guid"] = member.dependant.guid;
    tempClaimInitiation["member"] = member;
    props.setClaimInitiationPayload(tempClaimInitiation);
  };

  // ISS-048: products with a single predefined benefit shouldn't ask the user
  // to pick it. Select it as soon as the member is chosen; the field stays
  // visible (read only) so it is clear what the claim is being logged against.
  const onlyBenefit = selectableBenefits.length === 1
    ? selectableBenefits[0].type
    : null;

  React.useEffect(() => {
    if (!selectMember || !onlyBenefit) {
      return;
    }
    if (selectBenefit.length === 1 && selectBenefit[0] === onlyBenefit) {
      return;
    }
    setSelectBenefit([onlyBenefit]);
    applyBenefits(selectMember, [onlyBenefit]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMember, onlyBenefit]);

  if (!props.eventQuote) {
    return null;
  }


  const handleChange = (event) => {
    const { name, value } = event.target

    if (name === "selectmember") {
      setSelectMember(value)
      // Switching member must not carry the previous member's benefits over.
      // The effect above re-selects immediately when there is only one.
      setSelectBenefit([])
      applyBenefits(value, [])
    }

  }

  // Retest item 19: block a second claim for the same covered life here, at member
  // selection, rather than letting it run to assessment and zero out the benefit.
  // The customer journey has done this since ISS-046.
  const policyGuid = props.eventQuote ? props.eventQuote.policy_guid : undefined;
  // On OMK a rejection that has been confirmed closes the covered life, while one
  // still in Rejected_Pending does not — that claim can be sent back and re-decided,
  // so a fresh claim must stay possible. Other partners keep re-claiming after a
  // rejection.
  const claimGuardOptions = { rejectedClaimBlocks: getPartnerGuid() === 'omk' };
  const blockingClaimFor = (entry) =>
    findBlockingClaim(
      props.customerClaims,
      entry && entry.dependant ? entry.dependant.guid : null,
      policyGuid,
      claimGuardOptions
    );

  const dependantGuid =
    selectMember && selectMember.dependant ? selectMember.dependant.guid : null;
  const blockingClaim = findBlockingClaim(
    props.customerClaims,
    dependantGuid,
    policyGuid,
    claimGuardOptions
  );
  React.useEffect(() => {
    if (props.setAdditionalState) {
      props.setAdditionalState('member_has_open_claim', !!blockingClaim);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockingClaim, dependantGuid]);

  // The payable amount for this life after the BRD §5.7 claims-time caps.
  const selectedDependant =
    selectMember && selectMember.dependant ? selectMember.dependant : {};
  const primaryBenefit = selectableBenefits.length ? selectableBenefits[0] : null;
  const benefitCap = primaryBenefit
    ? applyBenefitCaps({
      benefitAmount: primaryBenefit.cover_amount_in_cents / 100,
      relationship: selectedDependant.relationship,
      dateOfBirth: selectedDependant.date_of_birth,
      dateOfDeath: props.eventDate,
      // The declaration is on the policy, not the dependant — and not on the event
      // quotation this screen is built from either, so the selected policy is what
      // has to be read. Without it no declared life was ever capped here.
      declaredMedicalCondition: declaredConditionForMember(
        selectedDependant,
        props.selectedPolicy
      ).declared,
      // Captured on the next step, so on a first pass this is blank and the banner
      // shows the cap; it lifts on the way back once an accident is recorded.
      causeOfDeath: props.causeOfDeath,
      coverStartDate:
        primaryBenefit.waiting_period_start_date ||
        (props.eventQuote ? props.eventQuote.start_date : undefined)
    })
    : null;


  const handleMember = (benefits) => {
    applyBenefits(selectMember, benefits);
  }


  const handleBenefitChange = (event) => {
    const {
      target: { value },
    } = event;
    
    let newValue = typeof value === 'string' ? value.split(',') : value;
    
    // Check if "Cash in Lieu of Service" is being selected
    const hasCashInLieu = newValue.includes('Cash in Lieu of Service');
    const hadCashInLieu = selectBenefit.includes('Cash in Lieu of Service');
    
    if (hasCashInLieu && !hadCashInLieu) {
      // If Cash in Lieu of Service is newly selected, only keep it
      newValue = ['Cash in Lieu of Service'];
    } else if (hasCashInLieu && hadCashInLieu) {
      // If Cash in Lieu of Service was already selected and other benefits are being added
      // Remove Cash in Lieu of Service and keep only the new selections
      newValue = newValue.filter(benefit => benefit !== 'Cash in Lieu of Service');
    } else if (!hasCashInLieu && hadCashInLieu) {
      // If Cash in Lieu of Service is being deselected, keep the new selections
      newValue = newValue.filter(benefit => benefit !== 'Cash in Lieu of Service');
    } else if (!hasCashInLieu && newValue.length > 0) {
      // If selecting other benefits, ensure Cash in Lieu of Service is not included
      newValue = newValue.filter(benefit => benefit !== 'Cash in Lieu of Service');
    }
    
    setSelectBenefit(newValue);
    handleMember(newValue);
    console.log('Selected benefits:', newValue);
  };
  const members = props.eventQuote.policy_schedule_entries ? props.eventQuote.policy_schedule_entries : []

  console.log("selected member", selectMember)


  return (
    <>

      <ValidatorForm
        onSubmit={() => { }}
      >
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} md={6} sm={6}>
            <SelectValidator
              labelId="select-member"
              id="select-member"
              value={selectMember}
              name='selectmember'
              type="text"
              label="Select Member"
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select a member']}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >

              {
                members.map((entry, index) => {
                  // A life that has already been claimed for cannot be picked at
                  // all, rather than being selectable and then failing on Next.
                  const memberBlockingClaim = blockingClaimFor(entry);
                  const alreadyClaimed = !!memberBlockingClaim;
                  return (
                    <MenuItem
                      key={index}
                      disabled={alreadyClaimed}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: alreadyClaimed ? "#999999" : "#000000",
                      }}
                      value={entry}>
                      {getFullName(entry.dependant)}
                      {alreadyClaimed && (
                        <span style={{ fontSize: '10px', color: '#8F191C', marginLeft: '6px' }}>
                          {blockingClaimLabel(memberBlockingClaim)}
                        </span>
                      )}
                    </MenuItem>
                  );
                })
              }

            </SelectValidator>
          </Grid>
          <Grid item xs={12} md={6} sm={6}>

            <FormControl sx={{ m: 1, width: "100%" }}>
              <InputLabel id="demo-multiple-chip-label">
                {onlyBenefit ? "Benefit" : "Select Benefits"}
              </InputLabel>
              <Select
                labelId="demo-multiple-chip-label"
                id="demo-multiple-chip"
                multiple
                disabled={!!onlyBenefit}
                value={selectBenefit}
                onChange={handleBenefitChange}
                input={
                  <OutlinedInput
                    id="select-multiple-chip"
                    label={onlyBenefit ? "Benefit" : "Select Benefits"}
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {
                  selectableBenefits
                    .map((benefit, index) => {
                      const isCashInLieu = benefit.type === 'Cash in Lieu of Service';
                      const hasCashInLieuSelected = selectBenefit.includes('Cash in Lieu of Service');
                      const hasOtherBenefitsSelected = selectBenefit.some(b => b !== 'Cash in Lieu of Service') && selectBenefit.length > 0;
                      
                      // Disable other benefits if Cash in Lieu is selected
                      // Disable Cash in Lieu if other benefits are selected
                      const isDisabled = (isCashInLieu && hasOtherBenefitsSelected) || (!isCashInLieu && hasCashInLieuSelected);
                      
                      return (
                        <MenuItem
                          key={index}
                          disabled={isDisabled}
                          style={{
                            ...getStyles(benefit, selectBenefit, theme),
                            opacity: isDisabled ? 0.5 : 1
                          }}
                          sx={{
                            fontFamily: "Open Sans",
                            fontStyle: "normal",
                            fontWeight: 300,
                            fontSize: "10px",
                            lineHeight: "16px",
                            letterSpacing: "0.005em",
                            color: isDisabled ? "#999999" : "#000000",
                          }}
                          value={benefit.type}
                        >
                          {benefit.type} --
                          {
                            benefit.type === "PHYSICAL CHECKUP" ? (
                              <>{benefit.benefit_period_cardinality} / {benefit.benefit_period_granuality}</>
                            ) : benefit.type === "TELE CONSULT" ? (
                              <>{benefit.benefit_period_cardinality} / {benefit.benefit_period_granuality}</>
                            ) : benefit.type === "HOSPITAL ADMISSION" ? (
                              <>
                                {currencyCode + " " + ((benefit.cover_amount_in_cents / benefit.cover_usage_count) / 100).toLocaleString()} / per night
                              </>
                            ) : (
                              <>Amount: {currencyCode}  {benefit.cover_amount_in_cents / 100}</>
                            )
                          }
                          {isDisabled && (
                            <span style={{ fontSize: '8px', color: '#999', marginLeft: '5px' }}>
                              {isCashInLieu ? '(Cannot select with other benefits)' : '(Cannot select with Cash in Lieu)'}
                            </span>
                          )}
                        </MenuItem>
                      );
                    })
                }
              </Select>
            </FormControl>
          </Grid>

          {blockingClaim && (
            <Grid item xs={12}>
              <p style={{ color: '#8F191C', fontSize: '12px', margin: '0 8px' }}>
                {blockingClaimMessage(
                  blockingClaim,
                  getFullName(selectMember.dependant)
                )}
              </p>
            </Grid>
          )}

          {/* What is actually payable for this life, after the BRD §5.7 caps. Nothing
              surfaced these before, so a child of 8 read as payable the full tier
              (retest items 17, 18). */}
          {benefitCap && benefitCap.capped && (
            <Grid item xs={12}>
              <p style={{ color: '#8a5300', fontSize: '12px', margin: '0 8px' }}>
                Registered cover is {currencyCode}{' '}
                {benefitCap.registered.toLocaleString()}; {currencyCode}{' '}
                {benefitCap.payable.toLocaleString()} is payable on this claim.{' '}
                {benefitCap.reason}.
              </p>
            </Grid>
          )}
        </Grid>
      </ValidatorForm >
    </>
  )
}

export default SelectMember